import type { StrategyRdMatch } from "../types/strategyRd";
import { asTime } from "./strategyRdWaveCLocation";

/** Bar times from match.meta that locate chart-pattern geometry (formation → signal). */
export function strategyRdConceptMetaGeometryBarTimes(
  meta: Record<string, unknown> | null | undefined
): string[] {
  if (!meta) return [];
  const keys = [
    "pattern_start_time",
    "pattern_end_time",
    "left_shoulder_time",
    "head_time",
    "right_shoulder_time",
    "span_start_time",
    "span_end_time",
    "cup_left_time",
    "cup_right_time",
    "cup_bottom_time",
    "handle_bar_time",
    "handle_time",
    "pole_start_time",
    "pole_end_time",
    "flag_start_time",
    "flag_end_time",
    "base_start_time",
    "base_end_time",
    "base_start_bar_time",
    "base_end_bar_time",
    "channel_start_time",
    "channel_end_time",
    "pattern_start_time",
    "pattern_end_time",
    "from_bar_time",
    "to_bar_time",
    "take_bar_time",
    "run_bar_time",
    "sweep_bar_time",
    "reclaim_bar_time",
  ] as const;
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: unknown) => {
    const t = asTime(raw);
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };
  for (const k of keys) push(meta[k]);
  const segments = meta.stage_segments;
  if (Array.isArray(segments)) {
    for (const row of segments) {
      if (row && typeof row === "object" && !Array.isArray(row)) {
        const seg = row as Record<string, unknown>;
        push(seg.from_bar_time);
        push(seg.to_bar_time);
      }
    }
  }
  const pivots = meta.pivots;
  if (Array.isArray(pivots)) {
    for (const p of pivots) {
      if (p && typeof p === "object" && !Array.isArray(p)) {
        push((p as Record<string, unknown>).bar_time);
      }
    }
  }
  const cupPath = meta.cup_path;
  if (Array.isArray(cupPath)) {
    for (const p of cupPath) {
      if (p && typeof p === "object" && !Array.isArray(p)) {
        push((p as Record<string, unknown>).bar_time);
      }
    }
  }
  for (const lineKey of ["upper_line", "lower_line", "pole", "price_line", "osc_line"] as const) {
    const line = meta[lineKey];
    if (!line || typeof line !== "object" || Array.isArray(line)) continue;
    const row = line as Record<string, unknown>;
    for (const end of [row.p1, row.p2]) {
      if (end && typeof end === "object" && !Array.isArray(end)) {
        push((end as Record<string, unknown>).bar_time);
      }
    }
  }
  push(meta.pivot1_time);
  push(meta.pivot2_time);
  push(meta.structure_break_pivot_time);
  push(meta.structure_break_bar_time);
  const markers = meta.markers;
  if (Array.isArray(markers)) {
    for (const m of markers) {
      if (m && typeof m === "object" && !Array.isArray(m)) {
        const row = m as Record<string, unknown>;
        push(row.bar_time ?? row.time ?? row.timestamp);
      }
    }
  }
  return out;
}

/** Bar times that define the concept geometry (formation + setup → entry). */
export function strategyRdConceptFrameBarTimes(
  match: StrategyRdMatch | null | undefined
): string[] {
  if (!match) return [];
  const times = [
    ...strategyRdConceptMetaGeometryBarTimes(
      match.meta && typeof match.meta === "object" && !Array.isArray(match.meta)
        ? (match.meta as Record<string, unknown>)
        : null
    ),
    match.bar_time,
    match.path?.setup_bar_time,
    match.path?.break_bar_time,
    match.path?.rejection_bar_time,
    match.path?.retest_bar_time,
    match.path?.entry_bar_time,
    ...(match.path?.events ?? []).map((e) => e.bar_time),
  ];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of times) {
    const t = typeof raw === "string" ? raw.trim() : "";
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

/**
 * Stable key so a later path enrich (entry-only) does not yank the viewport.
 * Candles + setup bar changing *does* re-guide.
 */
export function strategyRdConceptFrameGuideKey(
  candles: { timestamp?: string | null; time?: string | null }[] | null | undefined,
  match: StrategyRdMatch | null | undefined
): string | null {
  if (!match || !candles?.length) return null;
  const first = candles[0];
  const firstTime =
    (typeof first?.timestamp === "string" && first.timestamp.trim()) ||
    (typeof first?.time === "string" && first.time.trim()) ||
    "";
  const anchor = match.path?.setup_bar_time?.trim() || match.bar_time.trim();
  if (!anchor) return null;
  return `${firstTime}|${candles.length}|${anchor}`;
}

function nearestBarIndex(barTimesMs: readonly number[], timeMs: number): number {
  let best = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < barTimesMs.length; i += 1) {
    const d = Math.abs(barTimesMs[i]! - timeMs);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/**
 * Visible range around the concept — setup near the left with pad before it.
 * Default: 10 bars before earliest focus, 80 bars after latest (~91 total).
 * When formation → signal spans more than that, expand so both ends stay visible.
 */
export function strategyRdConceptVisibleRange(
  barTimesMs: readonly number[],
  focusTimesMs: readonly number[],
  opts?: { padLeft?: number; padRight?: number; minBars?: number; maxBars?: number }
): { from: number; to: number } | null {
  if (barTimesMs.length < 2) return null;
  const padLeft = opts?.padLeft ?? 10;
  const padRight = opts?.padRight ?? 80;
  const minBars = opts?.minBars ?? padLeft + 1 + padRight;
  /** Hard ceiling so a pathological focus span cannot load the whole lookback. */
  const hardCap = 1500;
  const maxBarsBase = opts?.maxBars ?? minBars;

  const indices: number[] = [];
  for (const t of focusTimesMs) {
    if (!Number.isFinite(t)) continue;
    const idx = nearestBarIndex(barTimesMs, t);
    if (idx >= 0) indices.push(idx);
  }
  if (!indices.length) return null;

  const minIdx = Math.min(...indices);
  const maxIdx = Math.max(...indices);
  const focusSpan = maxIdx - minIdx + 1;
  const maxBars = Math.min(hardCap, Math.max(maxBarsBase, focusSpan + padLeft + padRight));

  // Prefer setup (earliest focus) near the left: padLeft bars before it.
  let lo = Math.max(0, minIdx - padLeft);
  let hi = Math.min(barTimesMs.length - 1, Math.max(maxIdx + padRight, lo + minBars - 1));

  // If we hit the right edge, slide left so the window stays ~minBars.
  if (hi - lo + 1 < minBars && lo > 0) {
    lo = Math.max(0, hi - minBars + 1);
  }
  // Cap width at maxBars while keeping earliest focus inset from the left when possible.
  if (hi - lo + 1 > maxBars) {
    lo = Math.max(0, minIdx - padLeft);
    hi = Math.min(barTimesMs.length - 1, lo + maxBars - 1);
    if (hi - lo + 1 < maxBars && lo > 0) {
      lo = Math.max(0, hi - maxBars + 1);
    }
  }

  const from = barTimesMs[lo]!;
  const to = barTimesMs[hi]!;
  if (!(to > from)) return null;
  const slot =
    lo < hi
      ? Math.max(1, barTimesMs[lo + 1]! - barTimesMs[lo]!)
      : Math.max(1, (barTimesMs[1] ?? from + 60_000) - barTimesMs[0]!);
  return { from: from - slot / 2, to: to + slot / 2 };
}

/** Fit the whole OHLC window so many preview hits stay visible. */
export function strategyRdFullWindowVisibleRange(
  barTimesMs: readonly number[]
): { from: number; to: number } | null {
  if (barTimesMs.length < 2) return null;
  const from = barTimesMs[0]!;
  const to = barTimesMs[barTimesMs.length - 1]!;
  if (!(to > from)) return null;
  const slot = Math.max(1, barTimesMs[1]! - barTimesMs[0]!);
  return { from: from - slot / 2, to: to + slot / 2 };
}

/** Bars the Lab chart opens on: readable candles that fill the pane, not a whole window. */
export const STRATEGY_RD_OPENING_BARS = 150;

/**
 * The Lab chart's opening view: the most recent `bars` candles (all of them when fewer). A
 * backtest window can hold tens of thousands of bars; fitting them all draws sub-pixel sticks.
 */
export function strategyRdRecentVisibleRange(
  barTimesMs: readonly number[],
  bars: number = STRATEGY_RD_OPENING_BARS
): { from: number; to: number } | null {
  if (barTimesMs.length < 2) return null;
  const startIdx = Math.max(0, barTimesMs.length - Math.max(2, bars));
  const from = barTimesMs[startIdx]!;
  const to = barTimesMs[barTimesMs.length - 1]!;
  if (!(to > from)) return null;
  const slot = Math.max(1, barTimesMs[1]! - barTimesMs[0]!);
  // A little room on the right so the last candle isn't against the price axis.
  return { from: from - slot / 2, to: to + slot * 3 };
}
